import { useRef } from 'react';
import { CheckIcon, LightningIcon } from '@phosphor-icons/react';
import { StatusChip, cn, fmtWhen } from '@smena/ui';
import { ChannelMark, byChannelOrder } from './ChannelMark';
import type { ClientChannel, ReplyChannelState } from './api';

// Список «Куда отправить ответ»: одинаковый для поповера на десктопе и нижней шторки на
// телефоне. Содержимое одно, отличается только оболочка - иначе два списка разъедутся.
//
// Контейнер оболочки объявляет себя диалогом с заголовком; здесь - один listbox из опций,
// подзаголовки секций помечены presentation: по ARIA внутри listbox могут жить только опции.

export function ChannelPicker({
  channels,
  dormant,
  state,
  onPick,
  onAuto,
  labelledBy,
}: {
  /** Каналы, в которые гость писал: только они годятся в адресаты. */
  channels: ClientChannel[];
  /** Каналы без единого входящего - показываем, но выбрать нельзя. */
  dormant: ClientChannel[];
  state: ReplyChannelState;
  onPick: (channelRef: string) => void;
  onAuto: () => void;
  labelledBy: string;
}) {
  const isAuto = state.mode !== 'pinned';
  const auto = channels.find((c) => c.channelRef === state.auto);
  const sorted = channels.slice().sort(byChannelOrder);
  const listRef = useRef<HTMLDivElement>(null);

  // Роль listbox обещает клавиатуре стрелки и Home/End - без обработчика это обещание
  // ложное: скринридер объявляет список, а перемещаться по нему нечем.
  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    const options = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>('[role="option"]:not([aria-disabled="true"])') ?? [],
    );
    if (!options.length) return;
    e.preventDefault();
    const i = options.indexOf(document.activeElement as HTMLElement);
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? options.length - 1
          : Math.min(options.length - 1, Math.max(0, i + (e.key === 'ArrowDown' ? 1 : -1)));
    options[next]?.focus();
  }

  return (
    <div
      ref={listRef}
      role="listbox"
      aria-labelledby={labelledBy}
      onKeyDown={onKeyDown}
      className="max-h-64 overflow-y-auto"
    >
      <button
        type="button"
        role="option"
        aria-selected={isAuto}
        onClick={onAuto}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] transition-colors',
          isAuto ? 'bg-accent-soft' : 'hover:bg-bg',
        )}
      >
        <span
          className={cn(
            'grid size-6 shrink-0 place-items-center rounded-md',
            isAuto ? 'bg-accent text-white' : 'bg-panel2 text-muted',
          )}
          aria-hidden
        >
          <LightningIcon weight="fill" className="size-3.5" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="font-semibold">Автоматически</span>
          <span className="truncate text-[11px] text-muted">
            {auto
              ? `туда, откуда гость написал последним: ${auto.label}`
              : 'туда, откуда гость напишет'}
          </span>
        </span>
        {isAuto ? <CheckIcon weight="bold" className="size-4 shrink-0 text-accent-fg" /> : null}
      </button>

      <div role="presentation" className="mt-1 border-t border-line px-2.5 pt-2 pb-1 text-[11px] font-semibold text-muted">
        Или всегда отвечать в
      </div>

      {sorted.map((c) => {
        const down = c.status === 'down';
        const on = state.mode === 'pinned' && state.pinned === c.channelRef;
        return (
          <button
            key={c.channelRef}
            type="button"
            role="option"
            aria-selected={on}
            aria-disabled={down || undefined}
            title={down ? c.reason ?? 'канал недоступен' : undefined}
            onClick={down ? undefined : () => onPick(c.channelRef)}
            className={cn(
              'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] transition-colors',
              on ? 'bg-accent-soft' : down ? 'cursor-not-allowed' : 'hover:bg-bg',
            )}
          >
            <ChannelMark channel={c} size="lg" live />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className={cn('truncate font-semibold', down && 'text-muted')}>{c.label}</span>
              <span className="truncate text-[11px] text-muted">
                {[c.identity, c.lastInboundAt ? `гость писал ${fmtWhen(c.lastInboundAt)}` : 'гость не писал']
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
            {down ? (
              <StatusChip tone="red" className="shrink-0 text-[11px]" title={c.reason ?? undefined}>
                не работает
              </StatusChip>
            ) : on ? (
              <CheckIcon weight="bold" className="size-4 shrink-0 text-accent-fg" />
            ) : null}
          </button>
        );
      })}

      {dormant.length > 0 ? (
        <>
          <div role="presentation" className="mt-1 border-t border-line px-2.5 pt-2 pb-1 text-[11px] font-semibold text-muted">
            Гость ещё не открывал
          </div>
          {dormant.map((c) => (
            <div
              key={c.channelRef}
              role="option"
              aria-selected={false}
              aria-disabled
              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px]"
            >
              {/* Серая заливка, а не opacity: прозрачность осветляет фон под белым глифом
                  и роняет контраст ниже нормы, чего контраст-гейт по токенам не увидит. */}
              <ChannelMark channel={{ ...c, status: 'down' }} size="lg" live />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-semibold text-muted">{c.label}</span>
                <span className="truncate text-[11px] text-muted">
                  {c.identity ? `${c.identity} · ` : ''}увидит, только когда откроет ссылку
                </span>
              </span>
            </div>
          ))}
        </>
      ) : null}

    </div>
  );
}

/** Пояснение под списком. Вынесено ИЗ listbox: по ARIA там живут только опции. */
export function ChannelPickerFooter() {
  return (
    <div className="mt-1 border-t border-line px-2.5 pt-2 pb-1 text-[11px] leading-snug text-muted">
      Выбор запомнится для этого диалога. На Smena не влияет: она отвечает туда, откуда пришёл вопрос.
    </div>
  );
}
